import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, timer } from 'rxjs';
import { exhaustMap } from 'rxjs/operators';
import { PosTransaction, PosTransactionLine } from 'src/app/models/pos/pos-device';
import { PosTransactionStatus, PosTransactionType, posTransactionStatusColor, posTransactionStatusLabel } from 'src/app/models/enums/pos-enums';
import { PosTransactionService } from 'src/app/services/pos-device.service';
import { PosHubService } from 'src/app/services/pos-hub.service';
import { extractErrorMessage } from '../../../accounting/shared/e-invoice.helpers';

type Step = 'confirm' | 'waiting' | 'result';

/** Cihaz sonucu bu aralıkla yoklanır (iptal penceresiyle aynı). */
const POLL_MS = 2000;

/**
 * Onaylı satıştan satır iadesi (pos-entegrasyonu.md K24). Satırlar tamamen iade edilir; iade
 * önce karta, kart tutarı bitince nakit döner. Onaylanınca sunucu iade tutarını POS kasasından
 * (nakit kısmını nakit kasasından) çıkarır, müşteriyi borçlandırır ve iade edilen satırların
 * yasal stok çıkışını ters kayıtla geri alır; komisyon geri yazılmaz. Pencere beklerken
 * kapatılırsa işlem açık kalır; sonuç POS İşlemleri ekranında ve mutabakatta izlenir.
 */
@Component({
  selector: 'app-pos-refund-modal',
  templateUrl: './pos-refund-modal.component.html',
})
export class PosRefundModalComponent implements OnInit, OnDestroy {
  /** İade yapılacak onaylı satış. Satırları ve iade durumu ayrıntıdan yeniden okunur. */
  @Input() sale!: PosTransaction;

  readonly Status = PosTransactionStatus;
  readonly statusColor = posTransactionStatusColor;

  step: Step = 'confirm';
  loading = true;
  sending = false;
  cancelling = false;
  error: string | null = null;
  pollError: string | null = null;
  transaction: PosTransaction | null = null;

  lines: PosTransactionLine[] = [];
  /** Daha önce iade edilmiş ya da iadesi süren satırlar. */
  refundedLineNos = new Set<number>();
  selectedLineNos = new Set<number>();
  /** Seçilen satırların toplamı (TL); seçim değişince hesaplanır. */
  selectedTotal = 0;
  private poll?: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private service: PosTransactionService,
    private posHub: PosHubService,
  ) {}

  ngOnInit(): void {
    this.service.get(this.sale.id).subscribe({
      next: res => {
        this.loading = false;
        if (res.data) this.sale = res.data;
        this.lines = this.sale.lines || [];
        this.refundedLineNos = new Set(this.sale.refundedLineNos || []);
      },
      error: error => {
        this.loading = false;
        this.error = extractErrorMessage(error, 'Satışın satırları alınamadı.');
      },
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    // Sonuç pencerede görünüyordu; pencere kapandıysa genel POS bildirimi gelir.
    if (this.transaction) this.posHub.unwatch(this.transaction.id);
  }

  get isLinked(): boolean {
    return !!(this.sale.salesId || this.sale.salesGroupId);
  }

  /** Belgede nakit de varsa iade önce karta, artanı nakit döner. */
  get hasCash(): boolean {
    return (this.sale.cashPaymentAmount || 0) > 0;
  }

  get openLineCount(): number {
    return this.lines.filter(l => !this.refundedLineNos.has(l.lineNo)).length;
  }

  isRefunded(line: PosTransactionLine): boolean {
    return this.refundedLineNos.has(line.lineNo);
  }

  toggle(line: PosTransactionLine): void {
    if (this.isRefunded(line)) return;
    if (this.selectedLineNos.has(line.lineNo)) this.selectedLineNos.delete(line.lineNo);
    else this.selectedLineNos.add(line.lineNo);
    this.selectedTotal = this.lines
      .filter(l => this.selectedLineNos.has(l.lineNo))
      .reduce((total, l) => total + l.totalPrice, 0);
  }

  statusLabel(status: PosTransactionStatus): string {
    return posTransactionStatusLabel(PosTransactionType.Refund, status);
  }

  send(): void {
    if (!this.selectedLineNos.size) return;
    this.sending = true;
    this.error = null;
    this.service.refund(this.sale.id, [...this.selectedLineNos]).subscribe({
      next: res => {
        this.sending = false;
        this.transaction = res.data ?? null;
        if (this.transaction) this.posHub.watch(this.transaction.id);
        this.step = 'waiting';
        this.onTransaction();
      },
      error: error => {
        this.sending = false;
        this.error = extractErrorMessage(error, 'İade isteği cihaza gönderilemedi.');
      },
    });
  }

  private startPolling(): void {
    if (this.poll && !this.poll.closed) return;
    // exhaustMap: yavaş cevapta istekler üst üste binmez.
    this.poll = timer(POLL_MS, POLL_MS).pipe(
      exhaustMap(() => this.service.refresh(this.transaction!.id)),
    ).subscribe({
      next: res => {
        this.pollError = null;
        this.transaction = res.data ?? this.transaction;
        this.onTransaction();
      },
      error: error => {
        this.pollError = extractErrorMessage(error, 'Cihaz durumu alınamadı.');
        this.stopPolling();
      },
    });
  }

  private onTransaction(): void {
    const t = this.transaction;
    if (!t) return;
    if (!t.isFinal) {
      this.startPolling();
      return;
    }
    this.stopPolling();
    this.step = 'result';
  }

  checkNow(): void {
    this.pollError = null;
    this.startPolling();
  }

  /** Cihazda bekleyen iade isteğini geri çeker; teyit gelene kadar yoklama sürer. */
  cancelOnDevice(): void {
    if (!this.transaction) return;
    this.cancelling = true;
    this.service.cancel(this.transaction.id).subscribe({
      next: res => {
        this.cancelling = false;
        this.transaction = res.data ?? this.transaction;
        this.onTransaction();
      },
      error: error => {
        this.cancelling = false;
        this.pollError = extractErrorMessage(error, 'Geri çekme isteği gönderilemedi.');
      },
    });
  }

  get waitingText(): string {
    switch (this.transaction?.status) {
      case PosTransactionStatus.Pending: return 'İade isteği cihaza iletiliyor…';
      case PosTransactionStatus.InProgress: return 'Cihazda — iadenin cihazdan onaylanması bekleniyor.';
      case PosTransactionStatus.Suspended: return 'İşlem cihazda askıya alındı. Cihazdan devam ettirin ya da geri çekin.';
      default: return 'Sonuç bekleniyor…';
    }
  }

  get approved(): boolean {
    return this.transaction?.status === PosTransactionStatus.Approved;
  }

  /** Değişiklik olduysa (istek gönderildiyse) liste yenilensin diye işlem döner. */
  close(): void {
    this.stopPolling();
    if (this.transaction) this.activeModal.close(this.transaction);
    else this.activeModal.dismiss();
  }

  private stopPolling(): void {
    this.poll?.unsubscribe();
    this.poll = undefined;
  }
}
